import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { FINBillData } from '../../services/finBillDataApi';
import { useFinBillDataMode } from './hooks/useFinBillDataMode';
import { useFinBillDataRecord } from './hooks/useFinBillDataRecord';
import { useFinBillDataNavigation } from './hooks/useFinBillDataNavigation';
import { useFinBillDataLookup } from './hooks/useFinBillDataLookup';
import { useFinBillDataPermission } from './hooks/useFinBillDataPermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import ActionBar from './components/ActionBar';
import SearchModal from './components/SearchModal';
import { isFieldDisabledFn } from './helpers';
import './FinBillDataForm.css';

const FinBillDataForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchVisible, setSearchVisible] = useState(false);

  // Hooks
  const perms = useFinBillDataPermission();
  const { mode, setMode, isEditing } = useFinBillDataMode();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError,
  } = useFinBillDataRecord();
  const { lookups, loadLookups } = useFinBillDataLookup();

  // Navigation: wrap loadRecord
  const loadRecordById = useCallback(async (id: number) => {
    await loadRecord(id);
  }, [loadRecord]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useFinBillDataNavigation(loadRecordById);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        await loadLookups();
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordById(ids[0]);
        }
      } catch {
        // silently fail
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BR-005: BillType cascade
  const handleBillTypeChange = useCallback((val: string) => {
    setField('bill_type', val);
    if (val === 'AP') {
      setField('bill_sort', 'P');
    } else if (val === 'AR') {
      setField('bill_sort', 'R');
      setField('payee', '渤揚');
    }
  }, [setField]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: FINBillData) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.bill_mid);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除票據 #${record.bill_mid}（支票號碼: ${record.check_no}）？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordById(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordById, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Search handler
  const handleSearchSelect = useCallback(async (id: number) => {
    const idx = allIds.indexOf(id);
    if (idx >= 0) {
      setCurrentIdx(idx);
      await loadRecordById(id);
    } else {
      await loadRecordById(id);
      const ids = await loadList();
      const newIdx = ids.indexOf(id);
      if (newIdx >= 0) setCurrentIdx(newIdx);
    }
  }, [allIds, setCurrentIdx, loadRecordById, loadList]);

  const isFieldDisabled = useCallback((key: string) => isFieldDisabledFn(key, isEditing), [isEditing]);

  // Render
  return (
    <PermissionGuard module={Modules.FIN_BILLDATA} action="query">
    <div className="fin-billdata-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>財務管理</a>
              <span>/</span>
              <span className="current">票據管理</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>票據/支票管理</h1>
            <span className="badge badge-default">FIN_BILLDATA</span>
          </div>
          <div className="page-title-right">
            {record && (
              <span style={{ fontSize: 13, color: '#666' }}>
                #{record.bill_mid} | {record.check_no || '(無支票號碼)'}
              </span>
            )}
          </div>
        </div>

        {/* Form Content */}
        <div className="tabs">
          <div className="tab-pane active">
            <Tab1BasicInfo
              formData={formData}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              getFieldError={getFieldError}
              lookupBanks={lookups.banks}
              lookupCurrencies={lookups.currencies}
              lookupCompanies={lookups.companies}
              onBillTypeChange={handleBillTypeChange}
            />
          </div>
        </div>

        {/* Bottom Action Bar */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onSearch={() => setSearchVisible(true)}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
      </div>

      {/* Search Modal */}
      <SearchModal
        visible={searchVisible}
        onClose={() => setSearchVisible(false)}
        onSelect={handleSearchSelect}
      />
    </div>
    </PermissionGuard>
  );
};

export default FinBillDataForm;
